import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { User, Mail, Eye, EyeOff, Plus, Trash2 } from 'lucide-react'

import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import { fetchMyCustomersPDA, updateMyCustomerPDA, addMyCustomerPDA, deleteMyCustomerPDA } from '../actions/customers'
import { isFeatureLocked } from '../utils/permissions'
import '../styles/CustomerUsersView.css'

const MAX_USERS = 5

class CustomerPDAUsersView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      // Edit modal
      editingUser: null,
      editFullname: '',
      editEmail: '',
      editPassword: '',
      showEditPassword: false,
      saving: false,
      saveError: null,
      saveSuccess: false,
      // Create modal
      showCreateModal: false,
      newFullname: '',
      newEmail: '',
      newPassword: '',
      showNewPassword: false,
      creating: false,
      createError: null,
      // Delete confirm
      deletingUser: null,
      deleting: false,
      deleteError: null,
    }
  }

  componentDidMount() {
    this.props.fetchMyCustomersPDA()
  }

  openEdit(user) {
    this.setState({
      editingUser: user,
      editFullname: user.Fullname || '',
      editEmail: user.Email || '',
      editPassword: '',
      showEditPassword: false,
      saveError: null,
      saveSuccess: false,
    })
  }

  closeEdit() {
    this.setState({ editingUser: null })
  }

  async handleSave() {
    const { editingUser, editFullname, editEmail, editPassword } = this.state
    this.setState({ saving: true, saveError: null })
    const props = { Fullname: editFullname, Email: editEmail }
    if (editPassword) props.Password = editPassword
    try {
      const response = await this.props.updateMyCustomerPDA(editingUser._id, props)
      if (response && response.error) {
        this.setState({ saving: false, saveError: response.error.message || 'Error al guardar' })
      } else {
        this.setState({ saving: false, saveSuccess: true })
        setTimeout(() => { this.closeEdit(); this.props.fetchMyCustomersPDA() }, 1000)
      }
    } catch (e) {
      this.setState({ saving: false, saveError: 'Error al guardar' })
    }
  }

  openCreate() {
    this.setState({ showCreateModal: true, newFullname: '', newEmail: '', newPassword: '', showNewPassword: false, createError: null })
  }

  closeCreate() {
    this.setState({ showCreateModal: false })
  }

  async handleCreate() {
    const { newFullname, newEmail, newPassword } = this.state
    if (!newFullname.trim() || !newEmail.trim() || !newPassword.trim()) {
      return this.setState({ createError: 'Nombre, email y contraseña son obligatorios' })
    }
    this.setState({ creating: true, createError: null })
    try {
      const response = await this.props.addMyCustomerPDA({ Fullname: newFullname, Email: newEmail, Password: newPassword })
      if (response && response.error) {
        this.setState({ creating: false, createError: response.error.message || 'Error al crear usuario' })
      } else {
        this.setState({ creating: false })
        this.closeCreate()
        this.props.fetchMyCustomersPDA()
      }
    } catch (e) {
      this.setState({ creating: false, createError: 'Error al crear usuario' })
    }
  }

  openDelete(user) {
    this.setState({ deletingUser: user, deleteError: null })
  }

  closeDelete() {
    this.setState({ deletingUser: null })
  }

  async handleDelete() {
    const { deletingUser } = this.state
    this.setState({ deleting: true, deleteError: null })
    try {
      const response = await this.props.deleteMyCustomerPDA(deletingUser._id)
      if (response && response.error) {
        this.setState({ deleting: false, deleteError: response.error.message || 'Error al eliminar' })
      } else {
        this.setState({ deleting: false })
        this.closeDelete()
        this.props.fetchMyCustomersPDA()
      }
    } catch (e) {
      this.setState({ deleting: false, deleteError: 'Error al eliminar' })
    }
  }

  render() {
    const { myUsersLoading, myUsersError, myPdaUsers, role, type, category } = this.props
    const {
      editingUser, editFullname, editEmail, editPassword, showEditPassword, saving, saveError, saveSuccess,
      showCreateModal, newFullname, newEmail, newPassword, showNewPassword, creating, createError,
      deletingUser, deleting, deleteError
    } = this.state

    if (isFeatureLocked(role, type, category, 'my-pda-users')) {
      return (
        <div className="customer-users-view">
          <div className="customer-users-blocked">
            <div className="blocked-content">
              <i className="fas fa-lock fa-3x"></i>
              <h2>Función exclusiva de Licencia Pro</h2>
              <p>Actualiza tu licencia para gestionar tus usuarios PDA de forma autónoma.</p>
            </div>
          </div>
        </div>
      )
    }

    if (myUsersError) return <ErrorMessage message={myUsersError.message || 'Error al cargar usuarios'} />
    if (myUsersLoading || !myPdaUsers || !Array.isArray(myPdaUsers)) return <LoadingProgress />

    const canCreate = myPdaUsers.length < MAX_USERS

    return (
      <div className="customer-users-view">
        <div className="mt-4">
          <div className="users-header">
            <div className="header-content">
              <div className="header-text">
                <h1><i className="fas fa-tablet-alt mr-3"></i>Usuarios PDA</h1>
                <span className="subtitle-inline">Gestiona tus usuarios de terminal PDA</span>
              </div>
              <div className="users-limit-info">
                <span className={`limit-badge ${myPdaUsers.length >= MAX_USERS ? 'limit-full' : 'limit-ok'}`}>
                  <i className="fas fa-users mr-2"></i>
                  {myPdaUsers.length} / {MAX_USERS} usuarios
                </span>
              </div>
            </div>
            <div className="users-limit-notice">
              <i className="fas fa-info-circle mr-2"></i>
              Máximo <strong className="ml-1 mr-1"> {MAX_USERS} usuarios PDA </strong> por cuenta.
            </div>
          </div>

          <div className="users-container">
            <div className="users-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{myPdaUsers.length} {myPdaUsers.length === 1 ? 'usuario PDA asignado' : 'usuarios PDA asignados'}</span>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => this.openCreate()}
                disabled={!canCreate}
                title={!canCreate ? `Límite de ${MAX_USERS} usuarios alcanzado` : 'Añadir usuario PDA'}
              >
                <Plus size={14} className="mr-1" />Añadir usuario
              </button>
            </div>

            {myPdaUsers.length === 0 ? (
              <div className="no-results">
                <i className="fas fa-users fa-3x mb-3"></i>
                <h3>No tienes usuarios PDA</h3>
                <p>Pulsa «Añadir usuario» para crear el primero.</p>
              </div>
            ) : (
              <div className="users-grid">
                {myPdaUsers.map(user => (
                  <div key={user._id} className="user-card">
                    <div className="user-card-header user-card-header--pda">
                      <div className="user-avatar"><User size={24} /></div>
                      <div className="user-info">
                        <h3 className="user-name">{user.Fullname}</h3>
                        <p className="user-email"><Mail size={14} className="mr-1" />{user.Email}</p>
                      </div>
                    </div>
                    <div className="user-card-body">
                      <div className="user-details">
                        <div className="detail-item"><small><strong>ID:</strong> {user.number}</small></div>
                        <div className="detail-item"><small><strong>Tipo:</strong> {user.Type}</small></div>
                      </div>
                    </div>
                    <div className="user-card-footer" style={{ display: 'flex', gap: '8px' }}>
                      <span className="btn-edit" style={{ flex: 1 }} onClick={() => this.openEdit(user)}>
                        <i className="fas fa-edit mr-2"></i>Editar
                      </span>
                      <span className="btn-delete" onClick={() => this.openDelete(user)}>
                        <Trash2 size={14} />Eliminar
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Edit Modal */}
        {editingUser && (
          <div className="modal-overlay" style={{ position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1050 }}>
            <div className="modal-dialog" style={{ margin:0,maxWidth:'480px',width:'90%' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="fas fa-edit mr-2"></i>Editar: <strong>{editingUser.Fullname}</strong></h5>
                  <button type="button" className="close" onClick={() => this.closeEdit()}><span>&times;</span></button>
                </div>
                <div className="modal-body" style={{ padding:'24px' }}>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><User size={14} className="mr-2" />Nombre completo</label>
                    <input type="text" className="cu-form-input" value={editFullname} onChange={e => this.setState({ editFullname: e.target.value })} placeholder="Nombre del usuario" />
                  </div>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><Mail size={14} className="mr-2" />Email</label>
                    <input type="email" className="cu-form-input" value={editEmail} onChange={e => this.setState({ editEmail: e.target.value })} placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><i className="fas fa-lock mr-2"></i>Nueva contraseña <small>(dejar vacío para no cambiar)</small></label>
                    <div className="cu-password-input-wrapper">
                      <input type={showEditPassword ? 'text' : 'password'} className="cu-form-input" value={editPassword} onChange={e => this.setState({ editPassword: e.target.value })} placeholder="Nueva contraseña" />
                      <button type="button" className="cu-password-toggle" onClick={() => this.setState({ showEditPassword: !showEditPassword })}>
                        {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  {saveError && <div className="alert alert-danger mt-2">{saveError}</div>}
                  {saveSuccess && <div className="alert alert-success mt-2">¡Guardado correctamente!</div>}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => this.closeEdit()} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={() => this.handleSave()} disabled={saving || saveSuccess}>
                    {saving ? <span><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</span> : 'Guardar cambios'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" style={{ position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1050 }}>
            <div className="modal-dialog" style={{ margin:0,maxWidth:'480px',width:'90%' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><Plus size={16} className="mr-2" />Nuevo usuario PDA</h5>
                  <button type="button" className="close" onClick={() => this.closeCreate()}><span>&times;</span></button>
                </div>
                <div className="modal-body" style={{ padding:'24px' }}>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><User size={14} className="mr-2" />Nombre completo</label>
                    <input type="text" className="cu-form-input" value={newFullname} onChange={e => this.setState({ newFullname: e.target.value })} placeholder="Nombre del usuario" />
                  </div>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><Mail size={14} className="mr-2" />Email</label>
                    <input type="email" className="cu-form-input" value={newEmail} onChange={e => this.setState({ newEmail: e.target.value })} placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="cu-form-group">
                    <label className="cu-form-label"><i className="fas fa-lock mr-2"></i>Contraseña</label>
                    <div className="cu-password-input-wrapper">
                      <input type={showNewPassword ? 'text' : 'password'} className="cu-form-input" value={newPassword} onChange={e => this.setState({ newPassword: e.target.value })} placeholder="Contraseña" />
                      <button type="button" className="cu-password-toggle" onClick={() => this.setState({ showNewPassword: !showNewPassword })}>
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  {createError && <div className="alert alert-danger mt-2">{createError}</div>}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => this.closeCreate()} disabled={creating}>Cancelar</button>
                  <button className="btn btn-success" onClick={() => this.handleCreate()} disabled={creating}>
                    {creating ? <span><i className="fas fa-spinner fa-spin mr-2"></i>Creando...</span> : 'Crear usuario'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {deletingUser && (
          <div className="modal-overlay" style={{ position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1050 }}>
            <div className="modal-dialog" style={{ margin:0,maxWidth:'420px',width:'90%' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><Trash2 size={16} className="mr-2" />Eliminar usuario</h5>
                  <button type="button" className="close" onClick={() => this.closeDelete()}><span>&times;</span></button>
                </div>
                <div className="modal-body" style={{ padding:'24px' }}>
                  <p>¿Seguro que quieres eliminar a <strong>{deletingUser.Fullname}</strong>?</p>
                  <p className="text-muted"><small>Esta acción no se puede deshacer. El usuario perderá el acceso inmediatamente.</small></p>
                  {deleteError && <div className="alert alert-danger mt-2">{deleteError}</div>}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => this.closeDelete()} disabled={deleting}>Cancelar</button>
                  <button className="btn btn-danger" onClick={() => this.handleDelete()} disabled={deleting}>
                    {deleting ? <span><i className="fas fa-spinner fa-spin mr-2"></i>Eliminando...</span> : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  myPdaUsers: state.customers.myPdaUsers,
  myUsersLoading: state.customers.myUsersLoading,
  myUsersError: state.customers.myUsersError,
  role: state.auth.role,
  type: state.auth.type,
  category: state.auth.category,
})

const mapDispatchToProps = dispatch => ({
  fetchMyCustomersPDA: bindActionCreators(fetchMyCustomersPDA, dispatch),
  updateMyCustomerPDA: bindActionCreators(updateMyCustomerPDA, dispatch),
  addMyCustomerPDA: bindActionCreators(addMyCustomerPDA, dispatch),
  deleteMyCustomerPDA: bindActionCreators(deleteMyCustomerPDA, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(CustomerPDAUsersView)
